import React, { useState, useEffect } from 'react';
import { Users, TrendingUp, Activity, BarChart3, Calendar, CheckCircle2 } from 'lucide-react';
import { workloadApi } from '../lib/api';
import { EditorWorkloadCard } from '../components/EditorWorkloadCard';
import { EditorDetailModal } from '../components/EditorDetailModal';
import { SearchableSelect } from '../components/ui/SearchableSelect';

interface EditorWorkload {
  id: string;
  name: string;
  displayName: string;
  activeRequests: number;
  completedRequests: number;
  totalRequests: number;
  totalAssignedCreatives?: number;
  totalCompletedCreatives?: number;
  totalUploadedCreatives?: number;
  completionRate?: number;
  loadPercentage: number;
  status: string;
  maxConcurrentRequests: number | null;
  avgCompletionTimeHours: number;
  isAvailable: boolean;
  consecutiveMissedDeadlines?: number;
  escalationLevel?: number;
  lastEscalatedAt?: string | null;
}

interface WorkloadSummary {
  totalEditors: number;
  activeEditors: number;
  totalActiveRequests: number;
  totalAssignedCreatives?: number;
  totalCompletedCreatives?: number;
  averageLoad: number;
  overloadedEditors: number;
}

/** Get YYYY-MM-DD string for a date */
function toDateStr(d: Date): string {
  return d.toISOString().split('T')[0];
}

export function WorkloadDashboardPage() {
  const [summary, setSummary] = useState<WorkloadSummary | null>(null);
  const [editors, setEditors] = useState<EditorWorkload[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sortBy, setSortBy] = useState<'assigned' | 'active' | 'name'>('assigned');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedEditor, setSelectedEditor] = useState<string | null>(null);

  // Date range filter — default: last 30 days
  const [dateFrom, setDateFrom] = useState<string>(() => {
    const d = new Date(); d.setDate(d.getDate() - 30); return toDateStr(d);
  });
  const [dateTo, setDateTo] = useState<string>(toDateStr(new Date()));
  const [datePreset, setDatePreset] = useState<string>('30d');

  useEffect(() => {
    fetchWorkloadData();
  }, [dateFrom, dateTo]);

  const applyPreset = (preset: string) => {
    setDatePreset(preset);
    const now = new Date();
    const to = toDateStr(now);
    setDateTo(to);
    if (preset === '7d') {
      const d = new Date(); d.setDate(d.getDate() - 7); setDateFrom(toDateStr(d));
    } else if (preset === '30d') {
      const d = new Date(); d.setDate(d.getDate() - 30); setDateFrom(toDateStr(d));
    } else if (preset === '90d') {
      const d = new Date(); d.setDate(d.getDate() - 90); setDateFrom(toDateStr(d));
    } else if (preset === 'all') {
      setDateFrom('');
    }
  };

  const fetchWorkloadData = async () => {
    try {
      setLoading(true);
      setError('');
      const params: { from?: string; to?: string } = {};
      if (dateFrom) params.from = dateFrom;
      if (dateTo) params.to = dateTo;
      const response = await workloadApi.getOverview(params);
      setSummary(response.data.data.summary);
      setEditors(response.data.data.editors);
    } catch (err: any) {
      console.error('Failed to fetch workload data:', err);
      setError(err.response?.data?.error || 'Failed to load workload data');
    } finally {
      setLoading(false);
    }
  };

  const filteredAndSortedEditors = editors
    .filter(editor => {
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        return editor.name.toLowerCase().includes(query) ||
               editor.displayName?.toLowerCase().includes(query);
      }
      return true;
    })
    .sort((a, b) => {
      if (sortBy === 'assigned') return (b.totalAssignedCreatives ?? b.activeRequests ?? 0) - (a.totalAssignedCreatives ?? a.activeRequests ?? 0);
      if (sortBy === 'active') return b.activeRequests - a.activeRequests;
      if (sortBy === 'name') return a.name.localeCompare(b.name);
      return 0;
    });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading workload data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
          <BarChart3 className="text-blue-600" size={32} />
          Editor Workload Dashboard
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          Monitor editor capacity and manage workload distribution
        </p>
      </div>

      {error && (
        <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-red-600 dark:text-red-400">{error}</p>
        </div>
      )}

      {/* Date Range Filter */}
      <div className="bg-white dark:bg-gray-800 rounded-lg p-4 mb-6 border border-gray-200 dark:border-gray-700">
        <div className="flex flex-wrap gap-3 items-center">
          <Calendar className="text-gray-500" size={20} />
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Date Range:</span>

          {/* Quick presets */}
          <div className="flex gap-1">
            {[
              { key: '7d', label: 'Last 7 days' },
              { key: '30d', label: 'Last 30 days' },
              { key: '90d', label: 'Last 90 days' },
              { key: 'all', label: 'All Time' },
            ].map(p => (
              <button
                key={p.key}
                onClick={() => applyPreset(p.key)}
                className={`px-3 py-1.5 text-xs rounded-md border transition-colors ${
                  datePreset === p.key
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>

          <span className="text-gray-400 text-sm">or</span>

          {/* Custom date inputs */}
          <div className="flex items-center gap-2">
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => { setDateFrom(e.target.value); setDatePreset('custom'); }}
              className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
            />
            <span className="text-sm text-gray-500">to</span>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => { setDateTo(e.target.value); setDatePreset('custom'); }}
              className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
            />
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Total Editors</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                  {summary.totalEditors}
                </p>
              </div>
              <Users className="text-blue-600" size={28} />
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Active Requests</p>
                <p className="text-2xl font-bold text-purple-600 mt-1">
                  {summary.totalActiveRequests}
                </p>
              </div>
              <TrendingUp className="text-purple-600" size={28} />
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Assigned Creatives</p>
                <p className="text-2xl font-bold text-blue-600 mt-1">
                  {summary.totalAssignedCreatives ?? 0}
                </p>
              </div>
              <Activity className="text-blue-600" size={28} />
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Completed Creatives</p>
                <p className="text-2xl font-bold text-green-600 mt-1">
                  {summary.totalCompletedCreatives ?? 0}
                </p>
              </div>
              <CheckCircle2 className="text-green-600" size={28} />
            </div>
          </div>
        </div>
      )}

      {/* Filters and Search */}
      <div className="bg-white dark:bg-gray-800 rounded-lg p-4 mb-6 border border-gray-200 dark:border-gray-700">
        <div className="flex flex-wrap gap-4 items-center">
          <div className="flex-1 min-w-[200px]">
            <input
              type="text"
              placeholder="Search editors..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
            />
          </div>

          <div className="flex gap-2">
            <SearchableSelect
              value={sortBy}
              onChange={(v) => setSortBy(v as 'assigned' | 'active' | 'name')}
              className="w-52"
              options={[
                { value: 'assigned', label: 'Sort by Assigned Creatives' },
                { value: 'active', label: 'Sort by Active Requests' },
                { value: 'name', label: 'Sort by Name' },
              ]}
            />
          </div>

          <button
            onClick={fetchWorkloadData}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Refresh
          </button>
        </div>
      </div>

      {/* Escalation Banner — editors past 3 consecutive missed deadlines */}
      {(() => {
        const escalated = filteredAndSortedEditors.filter(e => (e.escalationLevel ?? 0) > 0 && (e.consecutiveMissedDeadlines ?? 0) >= 3);
        if (escalated.length === 0) return null;
        return (
          <div className="mb-6 rounded-lg border-2 border-red-400 bg-red-50 dark:bg-red-900/20 dark:border-red-700 p-4">
            <div className="flex items-start gap-3">
              <span className="text-2xl">🚨</span>
              <div className="flex-1">
                <h3 className="font-bold text-red-800 dark:text-red-200 text-base">
                  {escalated.length} editor{escalated.length > 1 ? 's' : ''} on escalation
                </h3>
                <p className="text-sm text-red-700 dark:text-red-300 mt-1">
                  These editors have missed 3 or more consecutive deadlines. Review their workload and reprioritise tasks as needed.
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {escalated.map(e => (
                    <span key={e.id} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-red-200 dark:bg-red-900/50 text-red-900 dark:text-red-200 text-xs font-semibold">
                      {e.displayName || e.name}
                      <span className="px-1.5 py-0.5 rounded bg-red-700 text-white text-[10px]">
                        L{e.escalationLevel} · {e.consecutiveMissedDeadlines || 0} misses
                      </span>
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Editor Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredAndSortedEditors.map(editor => (
          <EditorWorkloadCard
            key={editor.id}
            editor={editor}
            onViewDetails={() => setSelectedEditor(editor.id)}
            onRefresh={fetchWorkloadData}
          />
        ))}
      </div>

      {filteredAndSortedEditors.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-500 dark:text-gray-400">
            No editors found matching your filters
          </p>
        </div>
      )}

      {/* Editor Detail Modal */}
      {selectedEditor && (
        <EditorDetailModal
          editorId={selectedEditor}
          onClose={() => setSelectedEditor(null)}
          onUpdate={fetchWorkloadData}
        />
      )}
    </div>
  );
}
